import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTopicData } from "../hooks/use-topic-data";
import { useTrackDossierView } from "../hooks/useMemberDesk";
import { TopicHeader } from "../components/topic/TopicHeader";
import { LeakedExcerpts } from "../components/topic/LeakedExcerpts";
import { HiddenTruthSection } from "../components/topic/HiddenTruthSection";
import { DossierHero } from "../components/topic/DossierHero";
import { EvidenceGallery } from "../components/media/EvidenceGallery";
import { ConnectionsPanel } from "../components/topic/ConnectionsPanel";
import {
  ExecutiveSummarySection,
  EvidenceLibrarySection,
  RelatedDossiersSection,
} from "../components/topic/DossierSections";
import { CaseTimeline } from "../components/topic/CaseTimeline";
import { EntityConnections } from "../components/topic/EntityConnections";
import { InvestigationNetwork } from "../components/topic/InvestigationNetwork";
import { SourcesGrid } from "../components/topic/SourcesGrid";
import { TagList } from "../components/topic/TagList";
import { CommentsSection } from "../components/topic/CommentsSection";
import { SubscriberForm } from "../components/audience/SubscriberForm";
import { EditorialAdminInline } from "../components/dossier/EditorialAdminInline";
import { DossierTOC } from "../components/topic/DossierTOC";
import { EnvironmentalSeal } from "../components/EnvironmentalSeal";
import { EnvironmentalAtmosphere } from "../components/EnvironmentalAtmosphere";
import { setInvestigationTitle } from "../investigation/investigationMemory";
import { InvestigationContinues } from "../investigation/InvestigationContinues";
import { NewToThisTopic } from "../components/learning/NewToThisTopic";

const LoadingState = () => (
  <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
    <div className="flex items-center gap-3 text-[#757065] font-mono text-xs uppercase tracking-widest">
      <Loader2 className="animate-spin" size={14} /> RETRIEVING FILE…
    </div>
  </div>
);

const TopicDetail = () => {
  const { id } = useParams();
  const { topic, comments, loading, vote, myVote, voteBusy } = useTopicData(id);
  useTrackDossierView(topic?.slug);

  useEffect(() => {
    if (topic?.title) setInvestigationTitle(topic.title);
  }, [topic?.title]);

  // If the requested slug was retired via a dedupe merge, the backend
  // returns the canonical dossier with `redirected_from` set. Swap the
  // URL to the canonical slug via history.replaceState so refresh and
  // browser-back land on the surviving dossier — no 404, no loop.
  useEffect(() => {
    if (topic?.redirected_from && topic?.slug && id !== topic.slug) {
      window.history.replaceState(
        null,
        "",
        `/topic/${topic.slug}${window.location.search}${window.location.hash}`,
      );
    }
  }, [topic?.redirected_from, topic?.slug, id]);

  // Local override for editorial_modules so Admin Inline can mutate without
  // a full page reload. Falls back to whatever the topic endpoint returned.
  const [editorialOverride, setEditorialOverride] = useState(null);
  useEffect(() => { setEditorialOverride(null); }, [topic?.id]);
  const baseEditorial = editorialOverride || topic?.editorial_modules || null;

  // Oracle Continue-Investigation: lazily fetch the dynamic paths
  // payload and use it when it's stronger than whatever came bundled
  // on the topic doc. "Stronger" = a top dossier with a higher score.
  // Curated data still wins if the caller already populated it with
  // hand-picked leads (see /app/backend/oracle_paths.py).
  const [oraclePaths, setOraclePaths] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setOraclePaths(null);
    const slug = topic?.slug;
    if (!slug) return () => { cancelled = true; };
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/topics/${slug}/investigation-paths`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data && data.connections) {
          setOraclePaths(data);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [topic?.slug]);

  // Merge preference: oracle-derived wins only when the top score
  // beats the curated top score. Both branches use the SAME
  // ConnectionsPanel shape — the frontend never sees an "oracle mode"
  // toggle, and the API contract stays identical.
  const editorialModules = React.useMemo(() => {
    if (!baseEditorial && !oraclePaths?.connections) return baseEditorial;
    const curated = baseEditorial?.connections || null;
    const derived = oraclePaths?.connections || null;
    if (!curated && derived) {
      return { ...(baseEditorial || {}), connections: derived };
    }
    if (curated && derived) {
      const curatedTop = (curated.dossiers?.[0]?.score) ?? 0;
      const derivedTop = (derived.dossiers?.[0]?.score) ?? 0;
      if (derivedTop > curatedTop) {
        return { ...(baseEditorial || {}), connections: derived };
      }
    }
    return baseEditorial;
  }, [baseEditorial, oraclePaths]);

  if (loading) return <LoadingState />;
  if (!topic) return null;

  return (
    <div className="relative overflow-hidden">
      {/* Locked branding — dossier reading pages run:
          - EnvironmentalAtmosphere "reading" @ 0.35 intensity
            (warm paper-and-lamp ambience for long-form content)
          - EnvironmentalSeal "reading" @ 12 %
            (barely perceptible brand echo, recedes on scroll). */}
      <EnvironmentalAtmosphere variant="reading" intensity={0.35} />
      <EnvironmentalSeal
        intensity="reading"
        positionStyle={{
          top: "22vh",
          left: "50%",
          transform: "translate(-50%, 0)",
          width: "min(1200px, 104vh, 100vw)",
          height: "min(1200px, 104vh, 100vw)",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-3 sm:px-6 md:px-12 py-12" data-testid="topic-detail-page">
      <Link
        to="/archive"
        className="inline-flex items-center gap-2 text-[#A39E93] hover:text-[#D92D20] font-mono text-[10px] uppercase tracking-widest mb-8"
        data-testid="back-link"
      >
        <ArrowLeft size={12} /> Back to archive
      </Link>

      {/* 0. CINEMATIC HERO (renders only when the topic has AI artwork —
             which after Feb 2026 is every published dossier) */}
      <DossierHero topic={topic} />

      {/* 1. HERO */}
      <TopicHeader
        topic={topic}
        commentsCount={comments.length}
        onVote={vote}
        myVote={myVote}
        voteBusy={voteBusy}
      />

      {/* Like + Watch now live in the header's engagement row */}
      <EditorialAdminInline
        slug={topic.slug}
        modules={editorialModules}
        onUpdated={(m) => setEditorialOverride(m)}
      />

      {/* 2. EXECUTIVE SUMMARY */}
      <ExecutiveSummarySection topic={topic} />

      {/* 3. LEAKED EXCERPTS — directly beneath the summary so readers
             discover the classified material within the first minute. */}
      <LeakedExcerpts lines={topic.redacted_lines} />

      {/* 4. THE INVESTIGATION — main body ("The Hidden Truth") with the
             Case Snapshot briefing and automatic entity linking. */}
      <HiddenTruthSection text={topic.hidden_truth} slug={topic.slug} />

      {/* 5. CASE TIMELINE — the single investigative timeline with
             evidence badges (the years-only variant is retired). */}
      <CaseTimeline topic={topic} editorialModules={editorialModules} />

      {/* 6-8. KEY PEOPLE · ORGANIZATIONS · OPERATIONS — clickable
             intelligence files with hover/tap mini cards. */}
      <EntityConnections topic={topic} />

      {/* 9. EVIDENCE LIBRARY */}
      <EvidenceLibrarySection topic={topic} editorialModules={editorialModules} />

      {/* 9b. AUTHENTICATED PHOTOGRAPHS — published Media Library assets */}
      <EvidenceGallery slug={topic.slug} />

      {/* 10. SOURCES — trace the trail yourself */}
      <SourcesGrid sources={topic.sources} topicSlug={topic.slug} topicId={topic.id} />

      {/* 11. INVESTIGATION NETWORK — connected entity doorways */}
      <InvestigationNetwork topic={topic} />

      {/* 12. CONTINUE YOUR INVESTIGATION */}
      {!(editorialModules?.connections) && (
        <RelatedDossiersSection editorialModules={editorialModules} />
      )}
      <ConnectionsPanel editorialModules={editorialModules} />

      <TagList tags={topic.tags} />
      <CommentsSection slug={topic.slug} />

      {/* NEW TO THIS TOPIC? — optional starting points on advanced pages */}
      <NewToThisTopic />

      {/* CONTINUE YOUR INVESTIGATION — permanent V2.0 navigation signature */}
      <InvestigationContinues path={`/topic/${topic.slug}`} />

      <div className="mt-16">
        <SubscriberForm variant="dossier" topicSlug={topic.slug} />
      </div>

      <DossierTOC />
      </div>
    </div>
  );
};

export default TopicDetail;
